import { useEffect, useState } from "react";
import { Newspaper } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { HeadlineCard, AnnouncementCard, TopicFilter } from "@/components/NewsList";
import { NewsBoardCard } from "@/components/NewsBoardCard";

export default function NewsPublic() {
  const [topic, setTopic] = useState("all");
  const [feed, setFeed] = useState(null);
  const [posts, setPosts] = useState([]);

  useSeo({
    title: "Industry news — steel, freight, manufacturing, trade & tariffs | Unity & Utility",
    description: "Daily headlines on steel and metals, freight and logistics, manufacturing, trade and tariffs and workplace safety, with a plain-English line on what each one means for a small industrial business.",
    path: "/news",
    jsonLd: breadcrumbs([["Home", "/"], ["News", "/news"]]),
  });

  useEffect(() => {
    setFeed(null);
    api.get(`/news/headlines?topic=${topic}&limit=24`)
      .then(({ data }) => setFeed(data))
      .catch(() => setFeed({ items: [], unavailable: true }));
  }, [topic]);

  useEffect(() => {
    api.get("/news/posts?public_only=true").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  }, []);

  const shown = topic === "all" ? posts : posts.filter((p) => p.topic === topic);

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="news-public-page">
        <Crumbs items={[["Home", "/"], ["News", null]]} />
        <p className="overline text-[#B3241B]">Industry news</p>
        <h1 className="font-display mt-2 text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl">
          Steel, freight, trade and the factory floor
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
          Headlines pulled in through the day on steel and metals, freight and logistics, manufacturing,
          trade and tariffs and workplace safety — with a plain-English line on what each one means for a
          small industrial business. Plus our own announcements.
        </p>
        <div className="mt-6">
          <ShareBar url={`${window.location.origin}/news`} text="Industry news from Unity & Utility" testid="news-public-share" compact />
        </div>

        <TopicFilter value={topic} onChange={setTopic} testid="news-public-topics" />

        <div className="mt-6">
          <NewsBoardCard />
        </div>

        {shown.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="news-public-posts">
            {shown.map((post, i) => <AnnouncementCard key={post.id} post={post} index={i} canDelete={false} />)}
          </div>
        )}

        {feed?.unavailable && (
          <p data-testid="news-public-unavailable" className="mt-6 border-l-2 border-[#B3241B] bg-[#FFECEB] px-3 py-2 text-xs text-[#7A1712]">
            News sources are not reachable just now — try again in a moment.
          </p>
        )}
        {feed?.stale && (
          <p data-testid="news-public-stale" className="mt-6 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2 text-xs text-[#0F172A]">
            Showing the last headlines we had.
          </p>
        )}

        {!feed ? (
          <p className="py-16 text-center text-sm text-slate-500">Fetching the latest headlines…</p>
        ) : feed.items.length === 0 ? (
          <p data-testid="news-public-empty" className="mt-8 border border-border bg-white p-8 text-center text-sm text-slate-600">
            <Newspaper size={20} className="mx-auto mb-2 text-slate-400" /> No headlines to show yet.
          </p>
        ) : (
          <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="news-public-headlines">
            {feed.items.map((item, i) => <HeadlineCard key={`${item.url}-${i}`} item={item} index={i} />)}
          </div>
        )}
      </div>
    </PublicShell>
  );
}
